import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { ListItem } from "@/components/campaign/ListItem";
import { Select, SelectItem } from "@/components/generic/Select";
import { TagField } from "@/components/generic/TagField";
import { companyStatuses } from "@/data/company";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { ComparisonOperator, comparisonOperators } from "@/utils/operators";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { match, P } from "ts-pattern";
import { z } from "zod";

const schema = z.object({
  operator: comparisonOperators.enum,
  values: z.array(z.string()).min(1, {
    message: "Du skal vælge mindst en status",
  }),
});

export function CompanyStatusSegment({
  queryFilters,
  update,
  formId,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  const companyStatusFilter = queryFilters.value;

  const { control, handleSubmit, watch, setValue, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      operator:
        (companyStatusFilter?.operator as ComparisonOperator) || "Equal",
      values:
        companyStatusFilter?.values?.filter(
          (value, index, self) => self.indexOf(value) === index,
        ) || [],
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const { values, operator } = watch();

  const submit = handleSubmit((data) => {
    const consolidatedValues = data.values.reduce((acc: string[], value) => {
      return [...acc, value];
    }, []);

    const newCompanyStatusFilter = match({
      ...data,
      values: consolidatedValues,
    })
      .with({ operator: P.string, values: P.array(P.string) }, (filter) => ({
        operator: filter.operator,
        values: filter.values,
      }))
      .otherwise(() => undefined);

    update({
      value: newCompanyStatusFilter,
    });
  });

  const statuses = Object.entries(companyStatuses.values).map(
    ([key, value]) => ({
      id: key,
      label: value,
    }),
  );

  return (
    <>
      <SegmentHeader
        title="Virksomhedsstatus"
        description="Vælg de virksomhedsstatusser du ønsker skal indgå i din målgruppe, såsom 'Aktiv / Normal' eller 'Under Konkurs'."
        className="mb-9"
      />
      <Form
        onSubmit={submit}
        className="flex h-full flex-col items-center justify-between gap-9"
        id={formId}
      >
        <div className="flex flex-col gap-6">
          <div className="mx-auto flex gap-x-4">
            <Controller
              control={control}
              name="operator"
              render={({ field, fieldState: { error } }) => (
                <Select
                  placeholder="Operator"
                  aria-label="Operator"
                  selectedKey={field.value}
                  onSelectionChange={field.onChange}
                  items={Object.entries(comparisonOperators.values).map(
                    ([key, value]) => ({
                      id: key,
                      label: value,
                    }),
                  )}
                  errorMessage={error?.message}
                >
                  {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
              )}
            />
            <Controller
              control={control}
              name="values"
              render={({ field, fieldState: { error } }) => (
                <TagField
                  placeholder="Status"
                  aria-label="Status"
                  selectedOptions={field.value?.map((status) => ({
                    label:
                      companyStatuses.values[
                        status as keyof typeof companyStatuses.values
                      ],
                    value: status,
                  }))}
                  setSelectedOptions={(value) => {
                    if (Array.isArray(value))
                      return field.onChange(value.map((item) => item.value));
                  }}
                  options={statuses.map((status) => ({
                    label: status.label,
                    value: status.id,
                  }))}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
          <ul className="flex flex-col gap-y-2">
            {values.map((value, index) => (
              <ListItem
                key={value}
                label={`${operator === "Equal" ? "Lig med" : "Ikke lig med"} ${
                  companyStatuses.values[
                    value as keyof typeof companyStatuses.values
                  ]
                }`}
                onRemove={() => {
                  const newValues = [...values];
                  newValues.splice(index, 1);
                  setValue("values", newValues);
                }}
              />
            ))}
          </ul>
        </div>
      </Form>
    </>
  );
}
